import { zodResolver } from '@hookform/resolvers/zod';
import { PlusIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { LocationStatus, type LocationDTO } from '@invenco/common-interface/accounts';
import { AsnStatus, type AsnDTO } from '@invenco/supply-interface';

import { Button } from '@/components/core/button';
import { DialogBody, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { Separator } from '@/components/core/separator';
import { useDialogContext } from '@/components/dialog-with-context';
import { DatePickerFormField } from '@/components/forms/date-picker-form-field';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { TextareaFormField } from '@/components/forms/textarea-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import type { Result } from '@/shared/helpers/Result';
import { useQueryWithInput } from '@/shared/hooks/queries';

import {
  AsnAddressFields,
  asnAddressFieldsDefaultValues,
  asnAddressFieldsSchema,
} from '../asn-address-fields';

const baseSchema = asnAddressFieldsSchema.extend({
  locationName: z.string().trim(),
  expectedAt: z.date('Enter an expected date'),
  reference: z.string().trim(),
  note: z.string().trim(),
});

type Props = {
  asn: AsnDTO;
  onSubmit: (data: Partial<AsnDTO>) => Promise<Result> | Promise<void> | void;
};

function SectionHeading({ children }: { children: string }) {
  return <h3 className="font-heading text-sm font-semibold">{children}</h3>;
}

export function AsnEditDialogContent({ asn, onSubmit }: Props) {
  const { accountsGateway } = useGateways();
  const { isOpen } = useDialogContext();
  const [isAddingNotes, setIsAddingNotes] = useState(false);
  const { data: locations = [] } = useQueryWithInput({
    parentKey: 'locations',
    input: { status: LocationStatus.ACTIVE },
    query: (input, options) => accountsGateway.getLocations(input, options),
    select: ({ items }) => items as Partial<LocationDTO>[],
  });
  const canEditLocation = asn.status === AsnStatus.DRAFT;
  const isShowingNotes = isAddingNotes || Boolean(asn.note);
  const defaultValues = useMemo(
    () => ({
      locationName: asn.locationName ?? '',
      expectedAt: asn.expectedAt ? new Date(asn.expectedAt) : undefined,
      ...asnAddressFieldsDefaultValues,
      companyName: asn.fromAddress?.companyName ?? '',
      contactName: asn.fromAddress?.contactName ?? '',
      contactNumber: asn.fromAddress?.contactNumber ?? '',
      contactEmail: asn.fromAddress?.contactEmail ?? '',
      address1: asn.fromAddress?.address1 ?? '',
      address2: asn.fromAddress?.address2 ?? '',
      city: asn.fromAddress?.city ?? '',
      state: asn.fromAddress?.state ?? '',
      postcode: asn.fromAddress?.postcode ?? '',
      country: asn.fromAddress?.country ?? '',
      reference: asn.reference ?? '',
      note: asn.note ?? '',
    }),
    [asn],
  );

  const form = useForm<z.input<typeof baseSchema>>({
    resolver: zodResolver(baseSchema),
    defaultValues,
  });
  useEffect(() => {
    if (isOpen) {
      form.reset(defaultValues);
      setIsAddingNotes(Boolean(defaultValues.note));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the dialog opens; don't clobber in-progress edits
  }, [isOpen]);

  return (
    <FormDialogContent
      form={form}
      side="right"
      className="sm:max-w-xl lg:max-w-2xl"
      onSubmit={(data) => {
        return onSubmit({
          ...(canEditLocation && data.locationName ? { locationName: data.locationName } : {}),
          expectedAt: data.expectedAt.toISOString(),
          fromAddress: {
            companyName: data.companyName || undefined,
            contactName: data.contactName,
            contactNumber: data.contactNumber || undefined,
            contactEmail: data.contactEmail || undefined,
            address1: data.address1,
            address2: data.address2 || undefined,
            city: data.city || undefined,
            state: data.state || undefined,
            postcode: data.postcode || undefined,
            country: data.country,
          },
          reference: data.reference || null,
          // NOTE: the backend expects an empty string for omitted notes, unlike reference.
          note: data.note || '',
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>Edit {asn.name}</DialogTitle>
      </DialogHeader>
      <DialogBody className="gap-5">
        <section className="space-y-4">
          <SectionHeading>To warehouse</SectionHeading>
          <SelectFormField
            control={form.control}
            name="locationName"
            aria-label="Warehouse"
            placeholder="Select a warehouse"
            className="w-full"
            disabled={!canEditLocation}
          >
            {locations.map((location) =>
              location.name ? (
                <SelectItem key={location.id ?? location.name} value={location.name}>
                  {location.name}
                </SelectItem>
              ) : null,
            )}
          </SelectFormField>
        </section>

        <Separator />

        <section className="space-y-4">
          <SectionHeading>From address</SectionHeading>
          <AsnAddressFields />
        </section>

        <Separator />

        <section className="space-y-4">
          <SectionHeading>Expected date</SectionHeading>
          <DatePickerFormField
            control={form.control}
            name="expectedAt"
            aria-label="Expected date"
            description="Set the expected date that the shipment is expected to arrive at the warehouse."
          />
        </section>

        <Separator />

        <section className="space-y-4">
          <SectionHeading>Reference</SectionHeading>
          <TextFormField control={form.control} name="reference" aria-label="Reference" />
        </section>

        <Separator />

        <section className="space-y-4">
          {!isShowingNotes ? (
            <Button
              type="button"
              variant="ghost"
              className="-mx-2 w-[calc(100%+--spacing(4))] justify-start px-2"
              onClick={() => setIsAddingNotes(true)}
            >
              <PlusIcon />
              Add Notes
            </Button>
          ) : null}
          {isShowingNotes ? (
            <>
              <SectionHeading>Notes</SectionHeading>
              <TextareaFormField control={form.control} name="note" aria-label="Notes" rows={4} />
            </>
          ) : null}
        </section>
      </DialogBody>
    </FormDialogContent>
  );
}
